import {
  AfterViewInit,
  Component,
  ComponentRef,
  inject,
  Injector,
  Input,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { AbstractComponent } from '@sol/components/components/abstract.component';
import { DialogService, SolComponentEntry } from '@sol/components/services/dialog.service';
import { DIALOG_CLOSE } from '@sol/components/components/dialog/abstract-dialog-component.directive';
import { SolDialogContainerState } from '@sol/components/components/dialog/sol-dialog-container/sol-dialog-container.state';

/**
 * Lab-side dynamic dialog host.
 *
 * The visible stack/backdrop is owned by lab-dialogs; this component is the
 * runtime bridge that creates whichever lab dialog body the DialogService asks
 * for and registers the close callback.
 */
@Component({
  selector: 'lab-dialog-container',
  standalone: true,
  templateUrl: './lab-dialog-container.component.html',
  host: { class: 'lab-dialog-container-content' },
})
export class LabDialogContainerComponent extends AbstractComponent<SolDialogContainerState> implements AfterViewInit {
  private readonly dialogService = inject(DialogService);
  private readonly injector = inject(Injector);

  @ViewChild('dialogContent', { read: ViewContainerRef }) private dialogContainer!: ViewContainerRef;
  private componentRef: ComponentRef<any> | undefined;

  constructor() {
    super(SolDialogContainerState.of());
  }

  @Input()
  public set entry(entry: SolComponentEntry) {
    this.update(state => state.withEntry(entry));
  }

  public ngAfterViewInit(): void {
    this.updateDialogComponent();
  }

  private updateDialogComponent(): void {
    const entry = this.state.entry;
    if (!entry || !this.dialogContainer) {
      return;
    }

    if (this.dialogContainer.length === 0) {
      // The dynamic component needs the same close token as Sól dialogs so
      // AbstractDialogComponent.close() keeps working for lab dialog bodies.
      const injector = Injector.create({
        providers: [{ provide: DIALOG_CLOSE, useValue: () => this.dialogService.closeEntry(entry.id) }],
        parent: this.injector,
      });
      this.componentRef = this.dialogContainer.createComponent(entry.componentType, { injector });
      entry.configurer(this.componentRef.instance);
      this.dialogService.register(entry.id, this.componentRef.instance);
    }
  }
}
